import { type PassengerCount } from './types';
import type PriceClassExtraOption from './PriceClassExtraOption';
import { type ServiceDiscountTypes } from '@clients/AuthApi';

export default abstract class PriceClass {
    /**
     * coupon info to show in list and apply in prepay page
     *
     * @type {(null | { code: string; discount: number })}
     * @memberof TripPriceClass
     */
    couponCode: null | {
        code: string;
        discountPercent: number;
        maxDiscount: number;
    } = null;

    /**
     * Stores the selected prices extra options of the current price
     *
     * @type {PriceClassExtraOption[]}
     * @memberof TripPriceClass
     */
    selectedExtraOptions: PriceClassExtraOption[] = [];
    selectedExtraFormOptions: PriceClassExtraOption[] = [];

    isReservable = true;

    get servicesPreDiscountPercent() {
        const authStore = useAuthStore();
        return authStore.customDiscounts;
    }

    /**
     * Returns service discount
     *
     * @type {number}
     */
    abstract get prePriceDiscountPercent(): number;

    // net price that user should pay
    abstract get netPrice(): number;

    // amount of user custom prices
    get prePriceDiscountAmount() {
        return this.getPreDiscountAmount(this.netPrice);
    }

    get haveCustomDiscount() {
        return !!this.prePriceDiscountPercent;
    }

    get prePricePayable() {
        return this.getPriceWithPreDiscount(this.netPrice);
    }

    hasPriority = false;
    /**
     * Returns the capacity of trip
     *
     * @type {number}
     * @memberof TripPriceClass
     */
    abstract get capacity(): number;

    /**
     * Returns the payable price for a single adult passenger of trip
     *
     * @type {number}
     * @memberof TripPriceClass
     */
    get singlePayable(): number {
        return this.prePricePayable;
    }

    /**
     * Returns the total price (without considering discount) for a single adult passenger of trip
     *
     * @type {number}
     * @memberof TripPriceClass
     */
    abstract get singlePrice(): number;

    /**
     * If not null, specifies the url that must be fetched for terms of the service
     *
     * @type {string | null}
     * @memberof TripPriceClass
     */
    abstract get loadableTermsUrl(): string | null;

    /**
     * Returns the cancellation terms and conditions of the trip
     *
     * @type {string | null}
     * @memberof TripPriceClass
     */
    abstract get cancellationTerms(): string | null;

    /**
     * Returns the title of the trip
     *
     * @type {string}
     * @memberof TripPriceClass
     */
    abstract get title(): string;

    /**
     * Returns the total price (considering discount) to be checked with the amount retured from reservation
     *
     * @type {number | null}
     * @memberof TripPriceClass
     */
    abstract get totalPayableForCheck(): number | null;

    /**
     * Returns the total price (without considering discount) to be checked with the amount retured from reservation
     *
     * @type {number | null}
     * @memberof TripPriceClass
     */
    abstract get totalPriceForCheck(): number | null;

    /**
     * Returns the list of items of the trip price description
     *
     * @type {string}
     * @memberof TripPriceClass
     */
    abstract get descriptionItems(): string[];

    /**
     * check to is this price class have discount or not
     *
     * @readonly
     * @type {boolean}
     * @memberof PriceClass
     */
    get isDiscounted(): boolean {
        return (
            !!this.singlePayable &&
            !!this.singlePrice &&
            this.singlePrice > this.singlePayable
        );
    }

    // protected static customDiscounts: CustomDiscount | null = null;

    // public static configCustomDiscount(customDiscountsConfig: CustomDiscount) {
    //     PriceClass.customDiscounts = customDiscountsConfig;
    // }

    /**
     * Returns the extra options that may be applied to the price class
     */
    getAvailableExtraOptions(_count: PassengerCount): PriceClassExtraOption[] {
        return [];
    }

    getPreDiscountAmount(price: number) {
        return price * (this.prePriceDiscountPercent / 100);
    }

    getPriceWithPreDiscount(price: number) {
        return price - this.getPreDiscountAmount(price);
    }

    public static calculatePreDiscountPrice(
        price: number,
        serviceType: ServiceDiscountTypes
    ) {
        const authStore = useAuthStore();
        const { customDiscounts } = classToRefs(authStore);
        return customDiscounts.value[serviceType]
            ? price - price * (customDiscounts.value[serviceType] / 100)
            : price;
    }

    /**
     * Returns the extra options that may be applied to the price class in the form page
     */
    getAvailableFormExtraOptions(
        _count: PassengerCount
    ): PriceClassExtraOption[] {
        return [];
    }
}
